import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService } from '../../services';
import commissionMessagesService from '../../services/commissionMessagesService';
import CommissionForm from './CommissionForm';
import TaxForms from './TaxForms/TaxForms';
import CommissionMessagingModal from './CommissionMessagingModal';
import './CommissionsList.css';

const CommissionsList = () => {
  const { user } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [showTaxForms, setShowTaxForms] = useState(false);
  const [showMessagingModal, setShowMessagingModal] = useState(false);
  const [selectedCommissionForMessaging, setSelectedCommissionForMessaging] = useState(null);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const data = await commissionsService.getAll();
      setCommissions(data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionSubmit = (newCommission) => {
    fetchCommissions(); // Refresh the list
    setShowForm(false);
  };

  const handleMessageSent = () => {
    // Refresh commissions to update status
    fetchCommissions();
    setShowMessagingModal(false);
    setSelectedCommissionForMessaging(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-badge status-approved';
      case 'pending':
        return 'status-badge status-pending';
      case 'denied':
        return 'status-badge status-denied';
      case 'on_hold':
        return 'status-badge status-on-hold';
      case 'needs_reply':
        return 'status-badge status-needs-reply';
      default:
        return 'status-badge status-pending';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Filter commissions based on status, search term, and date range
  const filteredCommissions = commissions.filter(commission => {
    // Status filter
    if (filter !== 'all' && commission.status !== filter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        commission.reservation_number?.toLowerCase().includes(search) ||
        commission.guest_first_name?.toLowerCase().includes(search) ||
        commission.guest_last_name?.toLowerCase().includes(search) ||
        commission.supplier?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    // Date range filter
    if (dateRange.start && new Date(commission.travel_date) < new Date(dateRange.start)) return false;
    if (dateRange.end && new Date(commission.travel_date) > new Date(dateRange.end)) return false;
    
    return true;
  });

  // Calculate statistics
  const stats = {
    total: commissions.length,
    pending: commissions.filter(c => c.status === 'pending').length,
    approved: commissions.filter(c => c.status === 'approved').length,
    totalGross: commissions.reduce((sum, c) => sum + parseFloat(c.gross_commission || 0), 0),
    totalAgent: commissions.reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0),
    approvedAgent: commissions
      .filter(c => c.status === 'approved')
      .reduce((sum, c) => sum + parseFloat(c.agent_commission || 0), 0)
  };

  if (loading) {
    return (
      <div className="commissions-loading">
        <div className="loading-spinner"></div>
        <p>Loading commissions...</p>
      </div>
    );
  }

  return (
    <div className="commissions-container">
      {/* Header */}
      <div className="commissions-header">
        <div>
          <h2>Commission Management</h2>
          <p className="header-subtitle">Track and manage your commission requests</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => setShowTaxForms(true)}
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Tax Forms
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Submit New Commission
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-blue">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Commissions</p>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-yellow">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">Pending Approval</p>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-green">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">Approved</p>
            <p className="stat-value">{stats.approved}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-purple">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">Approved Commissions</p>
            <p className="stat-value">{formatCurrency(stats.approvedAgent)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="commissions-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search by reservation #, guest name, or supplier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="denied">Denied</option>
            <option value="on_hold">On Hold</option>
            <option value="needs_reply">Needs Reply</option>
          </select>
        </div>

        <div className="filter-group date-range">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            placeholder="Start date"
            className="date-input"
          />
          <span className="date-separator">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            placeholder="End date"
            className="date-input"
          />
        </div>
      </div>

      {/* Commissions Table */}
      <div className="commissions-table-container">
        <table className="commissions-table">
          <thead>
            <tr>
              <th>Date Submitted</th>
              <th>Reservation #</th>
              <th>Guest Name</th>
              <th>Supplier</th>
              <th>Travel Date</th>
              <th>Gross Commission</th>
              <th>Agent Commission</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCommissions.length === 0 ? (
              <tr>
                <td colSpan="9" className="empty-state">
                  {commissions.length === 0 ? (
                    <div>
                      <p>No commissions found</p>
                      <button 
                        className="btn btn-primary mt-2"
                        onClick={() => setShowForm(true)}
                      >
                        Submit Your First Commission
                      </button>
                    </div>
                  ) : (
                    <p>No commissions match your filters</p>
                  )}
                </td>
              </tr>
            ) : (
              filteredCommissions.map(commission => (
                <tr key={commission.id}>
                  <td>{formatDate(commission.created_at)}</td>
                  <td className="font-medium">{commission.reservation_number}</td>
                  <td>{commission.guest_first_name} {commission.guest_last_name}</td>
                  <td>{commission.supplier}</td>
                  <td>{formatDate(commission.travel_date)}</td>
                  <td className="text-right">{formatCurrency(commission.gross_commission)}</td>
                  <td className="text-right font-medium">{formatCurrency(commission.agent_commission)}</td>
                  <td>
                    <span className={getStatusBadgeClass(commission.status)}>
                      {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    {commission.status === 'needs_reply' ? (
                      <button
                        className="action-button"
                        onClick={() => {
                          setSelectedCommissionForMessaging(commission);
                          setShowMessagingModal(true);
                        }}
                        title="Reply"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L19 8M9 17l-5 5l-5-5" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        className="action-button"
                        onClick={() => setSelectedCommission(commission)}
                        title="View Details"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Commission Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <CommissionForm 
              onClose={() => setShowForm(false)}
              onSuccess={handleCommissionSubmit}
            />
          </div>
        </div>
      )}

      {/* Tax Forms Modal */}
      {showTaxForms && (
        <TaxForms 
          agentId={JSON.parse(localStorage.getItem('user') || '{}').id} 
          onClose={() => setShowTaxForms(false)} 
        />
      )}

      {/* Commission Messaging Modal */}
      {showMessagingModal && (
        <div className="modal-overlay" onClick={() => setShowMessagingModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <CommissionMessagingModal 
              commission={selectedCommissionForMessaging}
              onClose={() => setShowMessagingModal(false)}
              onMessageSent={handleMessageSent}
            />
          </div>
        </div>
      )}

      {/* Commission Details Modal */}
      {selectedCommission && (
        <div className="modal-overlay" onClick={() => setSelectedCommission(null)}>
          <div className="modal-content commission-details" onClick={(e) => e.stopPropagation()}>
            <div className="commission-details-header">
              <h3>Commission Details</h3>
              <button 
                onClick={() => setSelectedCommission(null)}
                className="close-button"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="commission-details-body">
              <div className="detail-section">
                <h4>Booking Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Reservation Number:</span>
                    <span className="detail-value">{selectedCommission.reservation_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Supplier:</span>
                    <span className="detail-value">{selectedCommission.supplier}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Booking Date:</span>
                    <span className="detail-value">{formatDate(selectedCommission.booking_date)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Travel Date:</span>
                    <span className="detail-value">{formatDate(selectedCommission.travel_date)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Guest Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Guest Name:</span>
                    <span className="detail-value">{selectedCommission.guest_first_name} {selectedCommission.guest_last_name}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Commission Details</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Gross Commission:</span>
                    <span className="detail-value">{formatCurrency(selectedCommission.gross_commission)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Agent Commission:</span>
                    <span className="detail-value commission-amount">{formatCurrency(selectedCommission.agent_commission)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={getStatusBadgeClass(selectedCommission.status)}>
                      {selectedCommission.status.charAt(0).toUpperCase() + selectedCommission.status.slice(1)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Submitted:</span>
                    <span className="detail-value">{formatDate(selectedCommission.created_at)}</span>
                  </div>
                </div>
              </div>

              {selectedCommission.notes && (
                <div className="detail-section">
                  <h4>Agent Notes</h4>
                  <p className="notes-content">{selectedCommission.notes}</p>
                </div>
              )}

              {selectedCommission.admin_notes && (
                <div className="detail-section">
                  <h4>Admin Notes</h4>
                  <p className="notes-content admin-notes">{selectedCommission.admin_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionsList;
